import {
  WHITEBOARD_COLORS,
  WHITEBOARD_HEIGHT,
  WHITEBOARD_WIDTH,
  WHITEBOARD_WIDTHS,
  type WhiteboardStroke,
} from '@veriverse/shared';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInteract } from '../net/interact.js';
import {
  chunk,
  newStrokeGroup,
  StrokeBuilder,
  strokesAt,
  toHalfPixels,
} from '../state/board-logic.js';
import { useBoardStore, type OpenWhiteboard } from '../state/board-store.js';
import { useSessionStore } from '../state/session-store.js';

type Tool = 'pen' | 'eraser';
const ERASER_RADIUS = 6;
const BOARD_BACKGROUND = '#ffffff';

/** A segment sent but not echoed back yet; drawn so the line does not flicker. */
interface PendingSegment {
  readonly group: string;
  readonly color: string;
  readonly width: number;
  readonly points: readonly number[];
  readonly at: number;
}
const PENDING_TTL_MS = 3_000;

function drawLine(
  ctx: CanvasRenderingContext2D,
  color: string,
  width: number,
  points: readonly number[],
) {
  if (points.length < 2) return;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  if (points.length === 2) {
    ctx.beginPath();
    ctx.arc((points[0] ?? 0) / 2, (points[1] ?? 0) / 2, width / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo((points[0] ?? 0) / 2, (points[1] ?? 0) / 2);
  for (let i = 2; i < points.length; i += 2)
    ctx.lineTo((points[i] ?? 0) / 2, (points[i + 1] ?? 0) / 2);
  ctx.stroke();
}

/**
 * Shared whiteboard (spec §6.15): pen with an 8-colour palette and 3 widths, an eraser for your own
 * strokes (any stroke for admins), undo of your last stroke, clear (admins, or anyone on a public
 * board) and PNG export. Every change goes to the server; the board shows what it echoes back.
 */
export function WhiteboardPanel({ board }: { board: OpenWhiteboard }) {
  const { t } = useTranslation();
  const me = useSessionStore((s) => s.user);
  const notice = useSessionStore((s) => s.interactionNotice);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<string>(WHITEBOARD_COLORS[0]);
  const [width, setWidth] = useState<number>(WHITEBOARD_WIDTHS[1]);
  const drawing = useRef<{ builder: StrokeBuilder; sent: boolean } | undefined>(undefined);
  const erasing = useRef<Set<string> | undefined>(undefined);
  const pending = useRef<PendingSegment[]>([]);
  const [, redraw] = useState(0);

  const close = () => useBoardStore.getState().closeWhiteboard();
  const isAdmin = me?.role === 'admin';
  const mayErase = (stroke: WhiteboardStroke) => isAdmin || stroke.authorId === me?.id;

  // Paint the board: saved strokes, segments on their way, and the line being drawn.
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const now = Date.now();
    const echoed = new Set(board.strokes.map((s) => `${s.group}:${s.points[0]},${s.points[1]}`));
    pending.current = pending.current.filter(
      (p) => now - p.at < PENDING_TTL_MS && !echoed.has(`${p.group}:${p.points[0]},${p.points[1]}`),
    );
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = BOARD_BACKGROUND;
    ctx.fillRect(0, 0, WHITEBOARD_WIDTH, WHITEBOARD_HEIGHT);
    for (const stroke of board.strokes) drawLine(ctx, stroke.color, stroke.width, stroke.points);
    for (const segment of pending.current)
      drawLine(ctx, segment.color, segment.width, segment.points);
    const live = drawing.current?.builder.pending();
    if (live) drawLine(ctx, color, width, live);
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') useBoardStore.getState().closeWhiteboard();
      if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        sendInteract(board.objectId, 'undo');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [board.objectId]);

  const boardPoint = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * WHITEBOARD_WIDTH) / rect.width,
      y: ((event.clientY - rect.top) * WHITEBOARD_HEIGHT) / rect.height,
    };
  };

  const sendSegment = (group: string, points: number[]) => {
    pending.current.push({ group, color, width, points, at: Date.now() });
    sendInteract(board.objectId, 'draw', { group, color, width, points });
  };

  const eraseAt = (x: number, y: number) => {
    for (const id of strokesAt(board.strokes, x, y, ERASER_RADIUS, mayErase)) {
      erasing.current?.add(id);
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = boardPoint(event);
    if (tool === 'eraser') {
      erasing.current = new Set();
      eraseAt(x, y);
      return;
    }
    const builder = new StrokeBuilder(newStrokeGroup());
    builder.add(...toHalfPixels(x, y));
    drawing.current = { builder, sent: false };
    redraw((n) => n + 1);
  };

  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    const { x, y } = boardPoint(event);
    if (erasing.current) {
      eraseAt(x, y);
      return;
    }
    const current = drawing.current;
    if (!current) return;
    const full = current.builder.add(...toHalfPixels(x, y));
    if (full) {
      sendSegment(current.builder.group, full);
      current.sent = true;
    }
    redraw((n) => n + 1);
  };

  const onPointerUp = () => {
    if (erasing.current) {
      for (const ids of chunk([...erasing.current], 20))
        sendInteract(board.objectId, 'erase', { ids });
      erasing.current = undefined;
      return;
    }
    const current = drawing.current;
    drawing.current = undefined;
    if (!current) return;
    const rest = current.builder.finish(current.sent);
    if (rest) sendSegment(current.builder.group, rest);
    redraw((n) => n + 1);
  };

  const clear = () => {
    if (window.confirm(t('whiteboard.confirmClear'))) sendInteract(board.objectId, 'clear');
  };

  const exportPng = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'whiteboard.png';
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="overlay" onClick={close}>
      <div
        className="panel whiteboard"
        role="dialog"
        aria-label={t('whiteboard.title')}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="board-toolbar">
          <div className="board-group" role="radiogroup" aria-label={t('whiteboard.tool')}>
            {(['pen', 'eraser'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={tool === value}
                className={tool === value ? '' : 'secondary'}
                onClick={() => setTool(value)}
              >
                {t(`whiteboard.${value}`)}
              </button>
            ))}
          </div>
          <div className="board-group" role="radiogroup" aria-label={t('whiteboard.color')}>
            {WHITEBOARD_COLORS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={color === value}
                aria-label={value}
                className={`swatch${color === value ? ' selected' : ''}`}
                style={{ background: value }}
                onClick={() => {
                  setColor(value);
                  setTool('pen');
                }}
              />
            ))}
          </div>
          <div className="board-group" role="radiogroup" aria-label={t('whiteboard.width')}>
            {WHITEBOARD_WIDTHS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={width === value}
                aria-label={t('whiteboard.widthPx', { px: value })}
                className={width === value ? '' : 'secondary'}
                onClick={() => {
                  setWidth(value);
                  setTool('pen');
                }}
              >
                <span className="width-dot" style={{ width: value + 2, height: value + 2 }} />
              </button>
            ))}
          </div>
          <div className="board-group">
            <button
              type="button"
              className="secondary"
              onClick={() => sendInteract(board.objectId, 'undo')}
            >
              {t('whiteboard.undo')}
            </button>
            {board.canClear && (
              <button type="button" className="secondary" onClick={clear}>
                {t('whiteboard.clear')}
              </button>
            )}
            <button type="button" className="secondary" onClick={exportPng}>
              {t('whiteboard.export')}
            </button>
            <button type="button" className="secondary" onClick={close}>
              {t('whiteboard.close')}
            </button>
          </div>
        </div>
        <canvas
          ref={canvasRef}
          className={`board-canvas tool-${tool}`}
          width={WHITEBOARD_WIDTH}
          height={WHITEBOARD_HEIGHT}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        {notice && <p className="error">{t(`interact.rejected.${notice}`)}</p>}
      </div>
    </div>
  );
}
